// Locale <-> route translation. Historically this module carried its own
// structural stand-in for vue-router's RawLocation so it would have no
// imports at all; that cost every caller an `as unknown as RawLocation` cast,
// so it now names the real type. RawLocation is used ONLY as a type, so
// TypeScript drops the import at emit and no runtime dependency is added.
// (Plain `import`, not `import type`: prettier 1.19 - what this repo pins -
// cannot parse `import type` and would silently skip linting this file.)
import { RawLocation } from 'vue-router'

export const LOCALES = ['ja', 'en', 'th'] as const

export const isValidLanguage = (lang: string | null | undefined): boolean =>
  lang != null && (LOCALES as readonly string[]).includes(lang)

// Splits a path into its pathname and the ?query/#hash that follows it.
// Callers pass full paths here - main.ts hands stripLang() a route.fullPath -
// so every segment operation below must stop at the query boundary. Reading
// the locale with a bare split('/') sees "en?utm=x" as one segment, which
// silently defeats both the strip and the idempotence check.
const splitSuffix = (path: string): [string, string] => {
  const i = path.search(/[?#]/)
  return i === -1 ? [path, ''] : [path.slice(0, i), path.slice(i)]
}

// Returns the locale segment of a pathname, or null when there is none.
const localeSegment = (pathname: string): string | null => {
  const first = pathname.split('/')[1]
  return isValidLanguage(first) ? first : null
}

// Returns the locale a path carries, or null when it carries none.
// Exported for the 404 branch of the lang guard: an unmatched route has empty
// params, so the guard has to read the locale off the raw path. Without it a
// 404-shaped URL that spells out its locale (#/ja/customerr) renders in
// whatever locale happened to be active already - the URL says ja while the
// page reads Thai - and the language menu then ticks the wrong entry, so
// clicking that entry looks like it does nothing.
export const langOfPath = (path: string): string | null =>
  localeSegment(splitSuffix(path)[0])

// Adds the locale prefix to a path. Idempotent: a path that already carries
// a locale is returned unchanged, which is what keeps ?next= from
// double-prefixing after a login redirect.
export function prefixPath(path: string, lang: string): string {
  const [pathname, suffix] = splitSuffix(path)
  if (localeSegment(pathname)) return path
  const rest =
    pathname === '/' ? '' : pathname.startsWith('/') ? pathname : `/${pathname}`
  return `/${lang}${rest}${suffix}`
}

// Removes the locale prefix. Used when persisting a path that must survive a
// locale change, e.g. the ?next= redirect target.
export function stripLang(path: string): string {
  const [pathname, suffix] = splitSuffix(path)
  if (!localeSegment(pathname)) return path
  const rest = pathname
    .split('/')
    .slice(2)
    .join('/')
  return `/${rest}${suffix}`
}

// Injects the active locale into a navigation target that omits it.
// MUST stay idempotent - withLang(withLang(x)) === withLang(x) - because
// call sites legitimately pass paths that already carry a locale
// (route.path, a stored ?next=), and a second prefix would 404.
//
// This is also the ONLY place that knows how to do it for every location
// shape, which is exactly what the guards need. A guard's next(location) runs
// through History.push/replace, NOT router.push/replace, so it bypasses
// wrapNavigation() in router.ts and has to carry the locale itself; when it
// does not, the lang guard rescues the URL with `replace: true` and swallows
// the history entry the navigation meant to add. Every guard therefore hands
// its target through here instead of prefixing by hand - hand-written
// versions have to pick the right shape (`path` gets a prefix, `name` gets a
// params.lang) and got it wrong once per shape. The lang guard runs before
// beforeEnter/beforeRouteEnter and only lets a navigation through once
// to.params.lang is a valid locale, so `to.params.lang` is always set at
// those call sites.
export function withLang(to: RawLocation, lang: string): RawLocation {
  if (typeof to === 'string') return prefixPath(to, lang)
  // `name` is checked BEFORE `path` to match vue-router's own
  // normalizeLocation, which returns as soon as it sees a name and never
  // looks at path. Checking path first would prefix a field nothing reads
  // and leave params.lang unset; the matcher then fills :lang from
  // currentRoute, which during a guard is still the route being LEFT - so
  // the locale asked for here would be silently swapped for the old one.
  // An explicit params.lang wins over the injected one.
  if (to.name) return { ...to, params: { lang, ...to.params } }
  if (to.path) return { ...to, path: prefixPath(to.path, lang) }
  // Query-only navigation: vue-router keeps the current path and params,
  // so the locale is already there. Touching it would be wrong.
  return to
}

// Rewrites an absolute route path as relative to its parent.
// Nested children in this codebase repeat the parent path
// (parent '/customer', child '/customer/:id/edit'), so the parent prefix has
// to be stripped as well or the path would be duplicated once nested.
export function toRelative(path: string, parentPath: string): string {
  if (path === parentPath) return ''
  if (parentPath && path.startsWith(`${parentPath}/`))
    return path.slice(parentPath.length + 1)
  return path.replace(/^\//, '')
}
